import React, { Suspense, useCallback } from "react";
import styles from "./SignUpFlow.module.css";
import { ErrorBoundary } from "@sentry/nextjs";
import { useWebTranslation } from "translations";
import { StepName } from "components/Signup/steps/types";
import { getSteps, stepMap } from "components/Signup/steps";
import ProgressBar from "components/ProgressBar/ProgressBar";
import Gutter from "ui-kit/Gutter";
import { ErrorComponent } from "components/PageTemplates/ErrorPage";
import { getOrg } from "utils/site";
import StripeProvider from "components/Signup/components/Payment/stripe/StripeProvider";
import SignupHeader from "../SignupHeader/SignupHeader";
import { useSignupStore } from "components/Signup/store/hooks";
import {
  goToNextStep,
  goToPreviousStep,
} from "components/Signup/store/slices/navigation";
import { useNavigation } from "components/Signup/store/selectors/navigation";
import { useStateStore } from "./useStateStore";
import { useRouteListener } from "./useRouteListener";
import { useAnimalRouting } from "./useAnimalRouting";

const steps = getSteps();

export const SignUpFlow = () => {
  const { t } = useWebTranslation();
  const { activeStep, isTransitioning, isValid } = useNavigation();

  useStateStore();
  useRouteListener();
  useAnimalRouting();

  const { dispatch } = useSignupStore();

  const { name } = steps.find((step) => step.name === activeStep) || steps[0];

  const StepComponent =
    typeof stepMap[name] === "object" ? stepMap[name][getOrg()] : stepMap[name];

  const nextStep = useCallback(
    async (replace = false, overrideStep?: StepName) => {
      dispatch(goToNextStep({ replace, overrideStep }));
    },
    [dispatch]
  );

  const previousStep = useCallback(
    async (replace = false) => {
      dispatch(goToPreviousStep({ replace }));
    },
    [dispatch]
  );

  return (
    <StripeProvider>
      <div className={styles.container}>
        <SignupHeader
          showPhoneButton={true}
          previous={previousStep}
          showBackButton={
            activeStep !== "thank-you" &&
            steps.findIndex((step) => step.name === activeStep) > 0
          }
        />
        <ProgressBar
          progress={Math.max(
            steps.findIndex((s) => s.name === activeStep) / (steps.length - 1),
            0.05
          )}
        />
        <ErrorBoundary
          fallback={
            <ErrorComponent
              title={t("500.title")}
              description={t("500.description")}
              imgSrc="/images/error-500.svg"
            />
          }
        >
          <Suspense>
            <StepComponent
              isValid={isValid}
              next={nextStep}
              previous={previousStep}
              transitioning={isTransitioning}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    </StripeProvider>
  );
};
